import { getApiClient } from '../api_client/get';
import { Container } from 'unstated';
import { getPersistentStore, getPersistentStoreMutex } from '@lemonadefashion/storage';
import { CartElement } from '@lemonadefashion/cart-element';
import { OrderPricing } from '@lemonadefashion/order';
import { ItemAvailability } from '@lemonadefashion/item-availability';

// Note for future reference:
// Many users may have data stored in localStorage's "cart" key.
// Many users may also have data stored in localStorage's "cart_replica" key.

const KEY = 'cart_info_v8';

// The pricing payload the API returns may carry `payout_unavailable_reason` (why some of the user's payouts could not
// be used on this order). OrderPricing does not expose it, so it is kept beside the object rather than on it: a
// WeakMap keeps OrderPricing's own type untouched and frees the entry along with the object.
const payoutUnavailableReasons = new WeakMap();

export function makeOrderPricing(payload) {
  if (payload == null) return null;
  const pricing = new OrderPricing(payload);
  payoutUnavailableReasons.set(pricing, payload.payout_unavailable_reason ?? null);
  return pricing;
}

export function getPayoutUnavailableReason(pricing) {
  return (pricing != null ? payoutUnavailableReasons.get(pricing) : null) ?? null;
}
function _getStore() { return getPersistentStore(); }
function _getMutex() { return getPersistentStoreMutex(); }

// This function is guaranteed not to acquire any locks or do any writes.
function getCartInfoFromStore() {
  try {
    const cartInfoJson = _getStore().getItem(KEY);
    if (cartInfoJson == null) return { elements: [], pricing: null };
    return JSON.parse(cartInfoJson);
  } catch (error) {
    console.log(error);
    return { elements: [], pricing: null };
  }
}

function setCartInfoInStore(elements, pricing) {
  return new Promise((resolve, reject) => {
    const store = _getStore();
    const mutex = _getMutex();
    mutex.lock(KEY)
      .then(() => {
        store.setItem(KEY, JSON.stringify({ elements: elements, pricing: pricing }));
        return mutex.release(KEY).then(resolve).catch(reject);
      })
      .catch(reject);
  });
}

function setCartPricingInfoInStore(pricing) {
  return new Promise((resolve, reject) => {
    const store = _getStore();
    const mutex = _getMutex();
    mutex.lock(KEY)
      .then(() => {
        const info = getCartInfoFromStore();
        store.setItem(KEY, JSON.stringify({ elements: info.elements, pricing: pricing }));
        return mutex.release(KEY).then(resolve).catch(reject);
      })
      .catch(reject);
  });
}

class CartContainer extends Container {
  constructor() {
    super();
    this.state = this._getUpdateFromStore();
    this._activeCartId = null;
    this._ephemeralSnapshot = null;
    // This will ensure state is synchronized with persistent store.
    // This event would only be fired if we're using localStorage internally.
    window.addEventListener('storage', (e) => {
      if (e.key !== KEY) return;
      this.setState(this._getUpdateFromStore());
    });
  }

  isEphemeralCartActive() {
    return this._ephemeralSnapshot != null;
  }

  getEphemeralElements() {
    return this._ephemeralSnapshot?.elements || [];
  }

  getEphemeralPricing() {
    return this._ephemeralSnapshot?.pricing || null;
  }

  getEphemeralPrimaryCurrencySellersShippingShares() {
    if (this._ephemeralSnapshot?.pricing) {
      return this._ephemeralSnapshot.pricing.getPrimaryCurrencySellersShippingShares() || {};
    }
    return {};
  }

  initialize() {
    if (this.shouldGoToNetwork()) {
      return this.loadFromNetwork();
    } else {
      return Promise.resolve();
    }
  }

  shouldGoToNetwork() {
    return true;
  }

  _getUpdateFromStore() {
    this._ephemeralSnapshot = null;
    const info = getCartInfoFromStore();
    try {
      return {
        elements: info.elements.map(x => new CartElement(x)),
        pricing: makeOrderPricing(info.pricing)
      };
    } catch (error) {
      console.log(error);
      return { elements: [], pricing: null };
    }
  }

  getPrimaryCurrencySellersShippingShares(options = {}) {
    const { useEphemeral = false } = options;
    const pricing = this._resolvePricing(useEphemeral);
    if (pricing) {
      return pricing.getPrimaryCurrencySellersShippingShares() || {};
    }
    return {};
  }

  _networkResolve(resolve, response) {
    this._ephemeralSnapshot = null;
    if (response.data.cart && response.data.cart_pricing) {
      // We should only resolve it when we have network-storage-state consistency.
      // If writing to persistent storage fails, we should still update memory-state.
      setCartInfoInStore(response.data.cart, response.data.cart_pricing)
        .catch(console.log)
        .finally(() => this.setState(this._getUpdateFromStore(), () => resolve(response)));
    } else {
      resolve(response);
    }
  }

  forceRefresh() {
    return this.loadFromNetwork();
  }

  loadFromNetwork(cartId = null) {
    return new Promise((resolve, reject) => {
      this._activeCartId = cartId || null;
      if (!cartId) this._ephemeralSnapshot = null;
      getApiClient().getCart(cartId || null)
        .then(this._networkResolve.bind(this, resolve))
        .catch(reject);
    });
  }

  _shouldUseEphemeral(requested) {
    if (this._ephemeralSnapshot == null) return false;
    return requested === true;
  }

  _resolveElements(useEphemeral) {
    if (this._shouldUseEphemeral(useEphemeral) && this._ephemeralSnapshot?.elements) {
      return this._ephemeralSnapshot.elements;
    }
    return this.state.elements;
  }

  _resolvePricing(useEphemeral) {
    if (this._shouldUseEphemeral(useEphemeral) && this._ephemeralSnapshot?.pricing) {
      return this._ephemeralSnapshot.pricing;
    }
    return this.state.pricing;
  }

  all(options = {}) {
    const { useEphemeral } = options;
    return this._resolveElements(useEphemeral);
  }

  size(options = {}) {
    const { useEphemeral } = options;
    return this._resolveElements(useEphemeral).length;
  }

  get(index, options = {}) {
    const { useEphemeral } = options;
    const elements = this._resolveElements(useEphemeral);
    if (index < 0 || index >= elements.length) throw new Error('Cart index out of bounds.');
    return elements[index];
  }

  getAllAvailableElements(options = {}) {
    const { useEphemeral } = options;
    return this._resolveElements(useEphemeral)
      .filter(x => x.getAvailability() === ItemAvailability.AVAILABLE_FOR_ORDER);
  }

  getActiveCartId() {
    if (this._activeCartId != null) return this._activeCartId;
    if (this._ephemeralSnapshot?.cartId != null) return this._ephemeralSnapshot.cartId;
    return null;
  }

  addItem(itemId, fabricId, extraOptionsData, sizeId, quantity = 1) {
    return new Promise((resolve, reject) => {
      getApiClient().addItemToCart(itemId, fabricId, extraOptionsData, sizeId, quantity)
        .then(this._networkResolve.bind(this, resolve))
        .catch(reject);
    });
  }

  getPricing(options = {}) {
    const { useEphemeral } = options;
    return this._resolvePricing(useEphemeral);
  }

  clearEphemeralSnapshot(options = {}) {
    const { reload = false } = options;
    if (!this._ephemeralSnapshot) return Promise.resolve();
    this._ephemeralSnapshot = null;
    this._activeCartId = null;
    if (reload) {
      return this.loadFromNetwork();
    }
    return new Promise(resolve => {
      this.setState(
        state => ({ ...state, _ephemeralVersion: (state._ephemeralVersion || 0) + 1 }),
        resolve);
    });
  }

  // This is only to be used for now in case separate containers receive information about an
  // updated cart pricing. This kind of thing will be obsolete when we have Redux.
  setPricing(pricing, options = {}) {
    const { persist = true, cartId } = options;
    if (cartId !== undefined) this._activeCartId = cartId;
    if (persist) {
      this._ephemeralSnapshot = null;
      return new Promise((resolve, reject) => {
        setCartPricingInfoInStore(pricing)
          .catch(console.log)
          .finally(() => this.setState(this._getUpdateFromStore(), resolve));
      });
    }
    return new Promise(resolve => {
      const snapshot = this._ephemeralSnapshot ?? {
        cartId: cartId ?? this._activeCartId,
        elements: this.state.elements
      };
      this._ephemeralSnapshot = {
        ...snapshot,
        cartId: cartId ?? snapshot.cartId,
        pricing: makeOrderPricing(pricing)
      };
      this.setState(state => ({ ...state, _ephemeralVersion: (state._ephemeralVersion || 0) + 1 }), resolve);
    });
  }

  setElementsAndPricing(elements, pricing, options = {}) {
    const { persist = true, cartId } = options;
    if (cartId !== undefined) this._activeCartId = cartId;
    if (persist) {
      this._ephemeralSnapshot = null;
      return new Promise((resolve, reject) => {
        setCartInfoInStore(elements, pricing)
          .catch(console.log)
          .finally(() => this.setState(this._getUpdateFromStore(), resolve));
      });
    }
    return new Promise(resolve => {
      try {
        this._ephemeralSnapshot = {
          cartId: cartId ?? this._activeCartId,
          elements: (elements || []).map(x => new CartElement(x)),
          pricing: makeOrderPricing(pricing)
        };
        this.setState(state => ({ ...state, _ephemeralVersion: (state._ephemeralVersion || 0) + 1 }), resolve);
      } catch (error) {
        console.log(error);
        resolve();
      }
    });
  }

  incQuantity(cartElementId) {
    return new Promise((resolve, reject) => {
      getApiClient().incCartItemQuantity(cartElementId)
        .then(this._networkResolve.bind(this, resolve))
        .catch(reject);
    });
  }

  decQuantity(cartElementId) {
    return new Promise((resolve, reject) => {
      getApiClient().decCartItemQuantity(cartElementId)
        .then(this._networkResolve.bind(this, resolve))
        .catch(reject);
    });
  }

  getElementById(cartElementId, options = {}) {
    const { useEphemeral = false } = options;
    const elements = this._resolveElements(useEphemeral);
    const result = elements.find(element => element.getId() === cartElementId);
    return result || null;
  }

  remove(cartElementId) {
    return new Promise((resolve, reject) => {
      getApiClient().removeFromCart(cartElementId)
        .then(this._networkResolve.bind(this, resolve))
        .catch(reject);
    });
  }
}

export default CartContainer;
